/**
 * LegalPages - Politică Confidențialitate (GDPR) + Termeni și Condiții +
 *              Politică de Anulare și Retragere
 * Pagini publice accesibile fără login. Cerință Netopia + ANSPDCP + ANPC.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Shield, FileText, RotateCcw } from "lucide-react";
import { ETESA_OPERATOR as OP } from "@/config/operator";
import LegalFooter from "@/components/LegalFooter";

const TAB_META = {
  privacy: {
    title: "Politica de Confidențialitate (GDPR) | e-Tesa",
    desc:
      "Politica de confidențialitate GDPR a platformei e-Tesa: ce date colectăm, cum le folosim, drepturile tale.",
    canonical: "https://e-tesa.ro/legal?tab=privacy",
  },
  terms: {
    title: "Termeni și Condiții | e-Tesa",
    desc: "Termenii și condițiile de utilizare ale platformei e-Tesa pentru firmele de construcții din România.",
    canonical: "https://e-tesa.ro/legal?tab=terms",
  },
  cancellation: {
    title: "Politică de Anulare și Retragere | e-Tesa",
    desc:
      "Politica de anulare a abonamentului și drept de retragere pentru clienții platformei e-Tesa.",
    canonical: "https://e-tesa.ro/legal?tab=cancellation",
  },
};

export default function LegalPages() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "privacy";
  const meta = TAB_META[defaultTab] || TAB_META.privacy;

  useEffect(() => {
    document.title = meta.title;
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", meta.desc);
    const c = document.querySelector('link[rel="canonical"]');
    if (c) c.setAttribute("href", meta.canonical);
  }, [defaultTab, meta]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <div className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600"
          data-testid="legal-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Înapoi
        </Button>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-blue-50">
            <h1 className="text-2xl font-bold text-slate-800">Informații Legale</h1>
            <p className="text-sm text-slate-500 mt-1">
              {OP.legal_name} · CUI {OP.cui} · {OP.reg_com} — Conformitate GDPR & ANPC
            </p>
          </div>

          <Tabs defaultValue={defaultTab} className="p-4 sm:p-6">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-6 gap-1 h-auto">
              <TabsTrigger value="privacy" data-testid="tab-privacy" className="data-[state=active]:bg-teal-50">
                <Shield className="w-4 h-4 mr-2" />
                <span className="text-xs sm:text-sm">Confidențialitate</span>
              </TabsTrigger>
              <TabsTrigger value="terms" data-testid="tab-terms" className="data-[state=active]:bg-teal-50">
                <FileText className="w-4 h-4 mr-2" />
                <span className="text-xs sm:text-sm">Termeni și Condiții</span>
              </TabsTrigger>
              <TabsTrigger value="cancellation" data-testid="tab-cancellation" className="data-[state=active]:bg-teal-50">
                <RotateCcw className="w-4 h-4 mr-2" />
                <span className="text-xs sm:text-sm">Anulare / Retragere</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="privacy">
              <ScrollArea className="h-[70vh] pr-4">
                <PrivacyPolicy />
              </ScrollArea>
            </TabsContent>
            <TabsContent value="terms">
              <ScrollArea className="h-[70vh] pr-4">
                <TermsOfService />
              </ScrollArea>
            </TabsContent>
            <TabsContent value="cancellation">
              <ScrollArea className="h-[70vh] pr-4">
                <CancellationPolicy />
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <div className="prose prose-slate max-w-none text-sm space-y-4" data-testid="privacy-policy-content">
      <h2 className="text-lg font-bold">Politica de Confidențialitate (GDPR)</h2>
      <p className="text-slate-500">Ultima actualizare: Februarie 2026</p>

      <h3 className="font-semibold text-base mt-6">1. Operatorul de date</h3>
      <p>
        Operatorul de date cu caracter personal pentru platforma <strong>{OP.brand}</strong> este:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>{OP.legal_name}</strong></li>
        <li>CUI: {OP.cui}</li>
        <li>Reg. Com.: {OP.reg_com} (EUID: {OP.euid})</li>
        <li>Sediu social: {OP.address_full}</li>
        <li>Email: <a href={`mailto:${OP.email}`} className="text-teal-600 hover:underline">{OP.email}</a></li>
        <li>Telefon: <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="text-teal-600 hover:underline">{OP.phone}</a></li>
      </ul>

      <h3 className="font-semibold text-base mt-6">2. Ce date colectăm</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Date de identificare:</strong> nume, prenume, email, telefon</li>
        <li><strong>Date profesionale:</strong> funcție, denumire firmă, CUI, șantier de lucru</li>
        <li><strong>Date de pontaj:</strong> ore lucrate, check-in/check-out, locații GPS la prezență</li>
        <li><strong>Date financiare:</strong> facturi emise/primite, devize, plăți, IBAN</li>
        <li><strong>Date de autentificare:</strong> parolă criptată (bcrypt), coduri de verificare email</li>
        <li><strong>Date tehnice:</strong> adresă IP, tip browser, log-uri de acces</li>
        <li><strong>Date de plată:</strong> NU stocăm date de card. Sunt procesate exclusiv de procesatorul de plăți autorizat.</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">3. Baza legală a prelucrării</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR): furnizarea serviciilor platformei</li>
        <li><strong>Obligație legală</strong> (Art. 6(1)(c) GDPR): raportare ANAF, e-Factura, contabilitate</li>
        <li><strong>Interes legitim</strong> (Art. 6(1)(f) GDPR): securitate, prevenire fraudă</li>
        <li><strong>Consimțământ</strong> (Art. 6(1)(a) GDPR): notificări email opționale, cookies non-esențiale</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">4. Cât timp păstrăm datele</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Date de cont: pe durata utilizării platformei + 30 zile după ștergere</li>
        <li>Pontaje și facturi: 10 ani (obligație legală — Codul Fiscal)</li>
        <li>Audit log: 5 ani</li>
        <li>Cookies de sesiune: până la deconectare</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">5. Destinatari ai datelor</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>ANAF</strong> — pentru e-Factura și raportări fiscale</li>
        <li><strong>demoanaf.ro</strong> — verificare date publice companii (CUI)</li>
        <li><strong>SendGrid</strong> — serviciu email transactional (UE)</li>
        <li><strong>OpenAI</strong> — funcții AI (extragere date, sumarizare)</li>
        <li><strong>MongoDB Atlas</strong> — stocare date (servere UE)</li>
        <li><strong>Procesator de plăți</strong> — exclusiv pentru tranzacțiile dvs. (Netopia / Stripe)</li>
      </ul>
      <p>Nu vindem și nu transferăm date personale către terți în scopuri de marketing.</p>

      <h3 className="font-semibold text-base mt-6">6. Drepturile dumneavoastră (GDPR)</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Dreptul de acces</strong> (Art. 15)</li>
        <li><strong>Dreptul la rectificare</strong> (Art. 16)</li>
        <li><strong>Dreptul la ștergere</strong> ("dreptul de a fi uitat", Art. 17)</li>
        <li><strong>Dreptul la restricționare</strong> (Art. 18)</li>
        <li><strong>Dreptul la portabilitate</strong> (Art. 20) — export JSON disponibil în Setări</li>
        <li><strong>Dreptul de opoziție</strong> (Art. 21)</li>
        <li><strong>Dreptul de a retrage consimțământul</strong> oricând</li>
      </ul>
      <p>
        Drepturile pot fi exercitate din <strong>Setări Cont &rsaquo; GDPR</strong> sau prin email la{" "}
        <a href={`mailto:${OP.dpo_email}`} className="text-teal-600 hover:underline">{OP.dpo_email}</a>.
        Răspundem în maxim 30 de zile.
      </p>

      <h3 className="font-semibold text-base mt-6">7. Securitate</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Parole criptate cu bcrypt</li>
        <li>Comunicare securizată HTTPS / TLS 1.3</li>
        <li>Izolare multi-tenant per companie</li>
        <li>Sanitizare DOMPurify (anti-XSS)</li>
        <li>Audit log pentru modificările de date sensibile</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">8. Cookies</h3>
      <p>
        Folosim doar <strong>cookies strict necesare</strong> (sesiune de autentificare). Banner-ul de
        cookies se afișează la prima vizită. Nu folosim cookies de tracking / publicitate.
      </p>

      <h3 className="font-semibold text-base mt-6">9. Plângeri</h3>
      <p>
        Puteți depune plângere la <strong>ANSPDCP</strong> (Autoritatea Națională de Supraveghere a
        Prelucrării Datelor cu Caracter Personal):{" "}
        <a href={OP.anspdcp_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
          {OP.anspdcp_url}
        </a>
      </p>
    </div>
  );
}

function TermsOfService() {
  return (
    <div className="prose prose-slate max-w-none text-sm space-y-4" data-testid="terms-content">
      <h2 className="text-lg font-bold">Termeni și Condiții de Utilizare</h2>
      <p className="text-slate-500">Ultima actualizare: Februarie 2026</p>

      <h3 className="font-semibold text-base mt-6">1. Părți contractuale</h3>
      <p>
        Acești Termeni reglementează utilizarea platformei <strong>{OP.brand}</strong> ({OP.website}),
        operată de <strong>{OP.legal_name}</strong> (CUI {OP.cui}, Reg. Com. {OP.reg_com}, sediu{" "}
        {OP.address_full}), denumită în continuare „Operatorul”, și utilizatorul final („Clientul”).
      </p>

      <h3 className="font-semibold text-base mt-6">2. Descrierea serviciului</h3>
      <p>
        e-Tesa este o platformă SaaS de management al afacerilor pentru firmele de
        construcții/servicii din România. Module: pontaj electronic, facturare, devize, gestiune
        documente, contracte, inventar, vehicule, proiecte, integrare ANAF e-Factura.
      </p>

      <h3 className="font-semibold text-base mt-6">3. Crearea contului</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Trebuie să furnizați informații corecte și actualizate</li>
        <li>Sunteți responsabil pentru securitatea parolei</li>
        <li>Trebuie să aveți minim 18 ani sau să acționați în numele unei persoane juridice</li>
        <li>Un cont este asociat unei singure firme</li>
        <li>La înregistrare trebuie să acceptați expres acești Termeni și Politica GDPR</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">4. Plata, abonament și credite</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Serviciul funcționează pe bază de credite (1 credit = 1 RON cu TVA inclus)</li>
        <li>Solicitarea de credite generează o <strong>proformă cu scadență 24 ore</strong></li>
        <li>După plată, proforma se transformă în factură fiscală</li>
        <li>Dacă proforma nu este achitată în 24 ore, accesul la acțiunile platite este suspendat automat</li>
        <li>Plățile sunt procesate de un procesator de plăți autorizat (Netopia / Stripe). Nu stocăm date de card.</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">5. Obligațiile Clientului</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Să utilizeze platforma conform legislației în vigoare</li>
        <li>Să nu acceseze date ale altor companii</li>
        <li>Să nu folosească platforma în scopuri ilegale, frauduloase sau de spam</li>
        <li>Să raporteze vulnerabilitățile de securitate identificate la {OP.email}</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">6. Date și proprietate intelectuală</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Datele introduse de Client îi aparțin Clientului</li>
        <li>Platforma e-Tesa și codul sursă sunt proprietatea Operatorului</li>
        <li>Clientul poate exporta datele oricând în format JSON (drept GDPR Art. 20)</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">7. Disponibilitate</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Platforma este oferită „ca atare” (as-is)</li>
        <li>Nu garantăm disponibilitate 100% (mentenanță planificată poate cauza întreruperi scurte)</li>
        <li>Backup-uri periodice; recomandăm export propriu pentru date critice</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">8. Răspundere</h3>
      <p>
        Operatorul nu este responsabil pentru pierderi indirecte sau pierderi de profit. Răspunderea
        maximă a Operatorului este limitată la suma plătită de Client în ultimele 12 luni.
      </p>

      <h3 className="font-semibold text-base mt-6">9. Anularea și rezilierea</h3>
      <p>
        Drepturile de retragere și rezilierea sunt detaliate în{" "}
        <a href="/legal?tab=cancellation" className="text-teal-600 hover:underline">Politica de Anulare și Retragere</a>.
      </p>

      <h3 className="font-semibold text-base mt-6">10. Modificarea termenilor</h3>
      <p>
        Operatorul poate modifica acești Termeni. Clienții vor fi notificați prin email și/sau
        notificare în platformă cu minim <strong>30 de zile</strong> înainte de intrarea în vigoare.
        Continuarea utilizării după acest termen constituie acceptare tacită.
      </p>

      <h3 className="font-semibold text-base mt-6">11. Legea aplicabilă și soluționarea litigiilor</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Acești Termeni sunt guvernați de legea română</li>
        <li>Litigiile pot fi soluționate amiabil sau prin <strong>ANPC – Platforma SOL</strong> (Soluționare Online a Litigiilor): <a href={OP.anpc_sol_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{OP.anpc_sol_url}</a></li>
        <li>Pentru litigii nesoluționate amiabil, competente sunt instanțele de la sediul Operatorului</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">12. Contact</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Email: <a href={`mailto:${OP.email}`} className="text-teal-600 hover:underline">{OP.email}</a></li>
        <li>Telefon: <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="text-teal-600 hover:underline">{OP.phone}</a></li>
        <li>Adresă: {OP.address_full}</li>
      </ul>
    </div>
  );
}

function CancellationPolicy() {
  return (
    <div className="prose prose-slate max-w-none text-sm space-y-4" data-testid="cancellation-content">
      <h2 className="text-lg font-bold">Politică de Anulare și Retragere</h2>
      <p className="text-slate-500">Ultima actualizare: Februarie 2026</p>

      <h3 className="font-semibold text-base mt-6">1. Dreptul legal de retragere (14 zile)</h3>
      <p>
        Conform OUG 34/2014 privind drepturile consumatorilor în contractele la distanță, Clientul
        persoană fizică are dreptul de a se retrage <strong>fără penalități și fără motiv</strong> în
        termen de <strong>14 zile calendaristice</strong> de la achiziție.
      </p>
      <p>
        <strong>Excepție:</strong> conform Art. 16 lit. m din OUG 34/2014, dacă Clientul a solicitat
        expres începerea prestării serviciului în acest termen și a folosit efectiv serviciul (ex: a
        generat devize / facturi care au consumat credite), <strong>dreptul de retragere se pierde</strong>
        proporțional cu serviciile deja prestate. Vom rambursa creditele neutilizate.
      </p>
      <p>
        <strong>Clienții persoane juridice (B2B):</strong> drepturile de retragere de mai sus nu se
        aplică automat (OUG 34/2014 vizează doar consumatorii). Pentru clienți B2B, se aplică
        condițiile contractului încheiat cu Operatorul.
      </p>

      <h3 className="font-semibold text-base mt-6">2. Cum exerciți dreptul de retragere</h3>
      <p>Trimite o cerere de retragere prin oricare dintre canalele:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Email la <a href={`mailto:${OP.support_email}`} className="text-teal-600 hover:underline">{OP.support_email}</a> cu subiect „Cerere retragere – CUI [CUI firmă]”</li>
        <li>Din platformă: <strong>Setări Cont &rsaquo; GDPR &rsaquo; Solicită ștergere</strong></li>
        <li>Telefon: <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="text-teal-600 hover:underline">{OP.phone}</a></li>
      </ul>
      <p>Cererea trebuie să conțină: numele firmei, CUI, email-ul contului și data achiziției.</p>

      <h3 className="font-semibold text-base mt-6">3. Rambursarea</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Rambursarea se face în <strong>maxim 14 zile</strong> de la primirea cererii valide</li>
        <li>Rambursarea se efectuează prin aceeași metodă de plată utilizată la achiziție</li>
        <li>Pentru plățile cu cardul, suma revine pe cardul cu care s-a făcut plata</li>
        <li>Suma rambursată = credite neutilizate × 1 RON (TVA inclus)</li>
        <li>Costurile bancare ale tranzacției NU sunt incluse în rambursare</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">4. Rezilierea contului (oricând)</h3>
      <p>
        Indiferent de dreptul legal de retragere, Clientul poate solicita oricând <strong>ștergerea
        contului</strong> din Setări &rsaquo; GDPR. Datele personale sunt eliminate / anonimizate în
        maxim 30 de zile, cu excepția datelor pe care suntem obligați să le păstrăm prin lege
        (facturi fiscale — 10 ani conform Codului Fiscal).
      </p>

      <h3 className="font-semibold text-base mt-6">5. Cazuri de neaplicare</h3>
      <p>Nu se acordă rambursare în următoarele situații:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Credite deja consumate pentru generare de devize, facturi, AI sau alte acțiuni</li>
        <li>Solicitări depuse peste 14 zile de la achiziție (pentru persoane fizice)</li>
        <li>Conturi suspendate pentru încălcarea Termenilor și Condițiilor</li>
        <li>Solicitări nedeclarate explicit ca cerere de retragere</li>
      </ul>

      <h3 className="font-semibold text-base mt-6">6. Soluționarea litigiilor</h3>
      <p>
        Dacă nu sunteți mulțumit de modul în care am soluționat cererea, puteți apela la:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>ANPC – Platforma SOL</strong>: <a href={OP.anpc_sol_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{OP.anpc_sol_url}</a></li>
        <li><strong>ANPC</strong> (Autoritatea Națională pentru Protecția Consumatorilor): <a href={OP.anpc_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{OP.anpc_url}</a></li>
      </ul>

      <h3 className="font-semibold text-base mt-6">7. Contact</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Email: <a href={`mailto:${OP.support_email}`} className="text-teal-600 hover:underline">{OP.support_email}</a></li>
        <li>Telefon: <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="text-teal-600 hover:underline">{OP.phone}</a></li>
        <li>Adresă: {OP.address_full}</li>
      </ul>
    </div>
  );
}
